import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "includeCharts",
    "pageOrientation", 
    "emailReport",
    "emailOptions",
    "emailRecipients",
    "emailSubject",
    "emailMessage"
  ]

  connect() {
    this.initializeDefaults()
  }

  initializeDefaults() {
    // Set default email subject if report has a name
    const reportBuilderElement = document.querySelector('[data-controller="report-builder"]')
    if (reportBuilderElement) {
      const reportName = this.getReportName()
      if (reportName && this.hasEmailSubjectTarget) {
        this.emailSubjectTarget.value = `Report: ${reportName}`
      }
    }
  }

  getReportName() {
    // Try to get report name from template or generate a default
    const templateNameElement = document.querySelector('[data-report-builder-target="templateName"]')
    if (templateNameElement && templateNameElement.value) {
      return templateNameElement.value
    }
    
    // Generate name based on data source and current date
    const dataSourceElement = document.querySelector('[data-report-builder-target="dataSource"]')
    if (dataSourceElement && dataSourceElement.value) {
      const dataSourceName = dataSourceElement.selectedOptions[0]?.textContent || dataSourceElement.value
      const date = new Date().toLocaleDateString()
      return `${dataSourceName} Report - ${date}`
    }
    
    return `Custom Report - ${new Date().toLocaleDateString()}`
  }

  // Quick export methods
  async exportPDF(event) {
    event.preventDefault()
    await this.performExport('pdf', {
      includeCharts: this.hasIncludeChartsTarget ? this.includeChartsTarget.checked : true,
      pageOrientation: this.getSelectedPageOrientation()
    })
  }

  async exportExcel(event) {
    event.preventDefault()
    await this.performExport('excel', {
      includeCharts: this.hasIncludeChartsTarget ? this.includeChartsTarget.checked : true
    })
  }

  async exportCSV(event) {
    event.preventDefault()
    await this.performExport('csv', {
      includeCharts: false // CSV doesn't support charts
    })
  }

  // Advanced export with all options
  async exportWithOptions(event) {
    event.preventDefault()
    
    const options = this.gatherExportOptions()
    const format = this.determineExportFormat(options)
    
    if (options.emailReport) {
      if (!this.validateEmailOptions(options)) {
        return
      }
      await this.performEmailExport(format, options)
    } else {
      await this.performExport(format, options)
    }
  }

  gatherExportOptions() {
    return {
      includeCharts: this.hasIncludeChartsTarget ? this.includeChartsTarget.checked : true,
      pageOrientation: this.getSelectedPageOrientation(),
      emailReport: this.hasEmailReportTarget ? this.emailReportTarget.checked : false,
      emailRecipients: this.hasEmailRecipientsTarget ? this.emailRecipientsTarget.value : '',
      emailSubject: this.hasEmailSubjectTarget ? this.emailSubjectTarget.value : '',
      emailMessage: this.hasEmailMessageTarget ? this.emailMessageTarget.value : ''
    }
  }

  determineExportFormat(options) {
    // Default to PDF for mobile exports with advanced options
    return 'pdf'
  }

  getSelectedPageOrientation() {
    const orientationInputs = document.querySelectorAll('[data-mobile-export-target="pageOrientation"]')
    for (const input of orientationInputs) {
      if (input.checked) {
        return input.value
      }
    }
    return 'portrait' // default
  }

  validateEmailOptions(options) {
    if (!options.emailRecipients || options.emailRecipients.trim() === '') {
      this.showError('Please enter at least one email recipient')
      return false
    }
    
    if (!options.emailSubject || options.emailSubject.trim() === '') {
      this.showError('Please enter an email subject')
      return false
    }
    
    // Validate email format
    const emails = options.emailRecipients.split(',').map(email => email.trim())
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    
    for (const email of emails) {
      if (!emailRegex.test(email)) {
        this.showError(`Invalid email address: ${email}`)
        return false
      }
    }
    
    return true
  }

  async performExport(format, options = {}) {
    try {
      this.showLoadingState(true)
      
      // Get report configuration from the report builder
      const reportBuilderElement = document.querySelector('[data-controller="report-builder"]')
      if (!reportBuilderElement) {
        throw new Error('Report builder not found')
      }
      
      const reportConfiguration = this.getReportConfiguration(reportBuilderElement)
      
      const response = await fetch('/api/reports/export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          format: format,
          configuration: reportConfiguration,
          options: options
        })
      })
      
      if (response.ok) {
        const result = await response.json()
        
        if (result.download_url) {
          // Trigger download
          this.triggerDownload(result.download_url, result.filename)
          this.showSuccess(`${format.toUpperCase()} export completed successfully`)
        } else {
          throw new Error('No download URL provided')
        }
      } else {
        const error = await response.json()
        throw new Error(error.message || `Failed to export ${format.toUpperCase()}`)
      }
    } catch (error) {
      console.error('Export failed:', error)
      this.showError(error.message || `Failed to export ${format.toUpperCase()}`)
    } finally {
      this.showLoadingState(false)
    }
  }

  async performEmailExport(format, options) {
    try {
      this.showLoadingState(true)
      
      const reportBuilderElement = document.querySelector('[data-controller="report-builder"]')
      if (!reportBuilderElement) {
        throw new Error('Report builder not found')
      }
      
      const reportConfiguration = this.getReportConfiguration(reportBuilderElement)
      
      const response = await fetch('/api/reports/email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          format: format,
          configuration: reportConfiguration,
          email_options: {
            recipients: options.emailRecipients.split(',').map(email => email.trim()),
            subject: options.emailSubject,
            message: options.emailMessage
          },
          export_options: {
            includeCharts: options.includeCharts,
            pageOrientation: options.pageOrientation
          }
        })
      })
      
      if (response.ok) {
        const result = await response.json()
        this.showSuccess('Report has been sent via email successfully')
        
        // Reset email form
        this.resetEmailForm()
      } else {
        const error = await response.json()
        throw new Error(error.message || 'Failed to send report via email')
      }
    } catch (error) {
      console.error('Email export failed:', error)
      this.showError(error.message || 'Failed to send report via email')
    } finally {
      this.showLoadingState(false)
    }
  }

  getReportConfiguration(reportBuilderElement) {
    // Try to get configuration from the report builder controller
    const controller = this.application.getControllerForElementAndIdentifier(reportBuilderElement, 'report-builder')
    if (controller && typeof controller.getConfiguration === 'function') {
      return controller.getConfiguration()
    }
    
    // Fallback: try to extract configuration from the element's data
    const configData = reportBuilderElement.dataset.reportBuilderReportConfigurationValue
    if (configData) {
      try {
        return JSON.parse(configData)
      } catch (e) {
        console.warn('Failed to parse report configuration from data attribute')
      }
    }
    
    throw new Error('Unable to get report configuration')
  }

  triggerDownload(url, filename) {
    // Create a temporary link element to trigger download
    const link = document.createElement('a')
    link.href = url
    link.download = filename || 'report'
    link.style.display = 'none'
    
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  resetEmailForm() {
    if (this.hasEmailRecipientsTarget) {
      this.emailRecipientsTarget.value = ''
    }
    if (this.hasEmailMessageTarget) {
      this.emailMessageTarget.value = ''
    }
    if (this.hasEmailReportTarget) {
      this.emailReportTarget.checked = false
      this.toggleEmailOptions()
    }
  }

  // UI Event Handlers
  toggleEmailOptions(event) {
    const isChecked = this.hasEmailReportTarget ? this.emailReportTarget.checked : false
    
    if (this.hasEmailOptionsTarget) {
      if (isChecked) {
        this.emailOptionsTarget.style.display = 'block'
        // Focus on first input for better UX
        setTimeout(() => {
          if (this.hasEmailRecipientsTarget) {
            this.emailRecipientsTarget.focus()
          }
        }, 300)
      } else {
        this.emailOptionsTarget.style.display = 'none'
      }
    }
  }

  showLoadingState(isLoading) {
    const exportButtons = this.element.querySelectorAll('button[data-action*="export"]')
    
    exportButtons.forEach(button => {
      if (isLoading) {
        button.disabled = true
        const originalText = button.innerHTML
        button.dataset.originalText = originalText
        button.innerHTML = `
          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          Exporting...
        `
      } else {
        button.disabled = false
        if (button.dataset.originalText) {
          button.innerHTML = button.dataset.originalText
          delete button.dataset.originalText
        }
      }
    })
  }

  showSuccess(message) {
    this.showAlert(message, 'success')
  }

  showError(message) {
    this.showAlert(message, 'danger')
  }

  showAlert(message, type) {
    // Remove existing alerts
    const existingAlerts = this.element.querySelectorAll('.alert')
    existingAlerts.forEach(alert => alert.remove())

    // Create new alert
    const alert = document.createElement('div')
    alert.className = `alert alert-${type} alert-dismissible fade show mt-3`
    alert.innerHTML = `
      ${message}
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `
    
    // Insert at the top of the card body
    const cardBody = this.element.querySelector('.card-body')
    if (cardBody) {
      cardBody.insertBefore(alert, cardBody.firstChild)
    }
    
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      if (alert.parentNode) {
        alert.remove()
      }
    }, 5000)
  }
}